import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Factory, IndianRupee, Users, FileBarChart, CreditCard, Settings, Settings2, LogOut, UserCog, Shield, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState('staff');
  const [username, setUsername] = useState('');

  useEffect(() => {
    const role = localStorage.getItem('userRole') || 'staff';
    setUserRole(role);
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setUsername(user.username || 'User');
    } catch {
      setUsername('User');
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  const isAdmin = userRole === 'admin';

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', showFor: 'all' },
    { path: '/production', icon: Factory, label: 'Production', showFor: 'all' },
    { path: '/materials-inward', icon: Package, label: 'Materials Inward', showFor: 'all' },
    { path: '/sales', icon: IndianRupee, label: 'Sales', showFor: 'all' },
    { path: '/pending-payments', icon: CreditCard, label: 'Pending Payments', showFor: 'all' },
    { path: '/workers', icon: Users, label: 'Workers', showFor: 'all' },
    { path: '/reports', icon: FileBarChart, label: 'Reports', showFor: 'all' },
    { path: '/control-panel', icon: Settings2, label: 'Control Panel', showFor: 'all' },
    { path: '/settings', icon: Settings, label: 'Settings', showFor: 'all' },
    { path: '/staff-management', icon: UserCog, label: 'Staff Management', showFor: 'admin' }
  ];

  const visibleNavItems = navItems.filter(item => 
    item.showFor === 'all' || (item.showFor === 'admin' && isAdmin)
  );

  return (
    <div className="flex min-h-screen bg-[#F5F5F0]">
      <div className="noise-overlay" />
      
      <aside className="w-64 bg-white border-r border-concrete-200 shadow-sm relative z-20 flex flex-col">
        <div className="p-6 border-b border-concrete-200">
          <h1 className="font-manrope font-bold text-2xl text-brick-600" data-testid="sidebar-title">DHAYA BRICKS</h1>
          <p className="text-xs font-inter text-concrete-600 mt-1">Production ERP</p>
        </div>

        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-colors ${
                  isActive
                    ? 'bg-brick-600 text-white shadow-md'
                    : 'text-concrete-600 hover:bg-brick-50 hover:text-brick-700'
                }`}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-concrete-200">
          <div className="flex items-center gap-2 px-4 py-2 mb-2">
            <div className={`p-1.5 rounded-full ${isAdmin ? 'bg-amber-100' : 'bg-blue-100'}`}>
              <Shield className={`h-4 w-4 ${isAdmin ? 'text-amber-600' : 'text-blue-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-concrete-800 truncate">{username}</p>
              <p className={`text-xs font-medium ${isAdmin ? 'text-amber-600' : 'text-blue-600'}`}>
                {isAdmin ? 'Admin' : 'Staff'}
              </p>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-concrete-600 hover:bg-brick-50 hover:text-brick-700"
          >
            <LogOut className="mr-3 h-5 w-5" />
            Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-8 relative z-10">
        <Outlet />
      </main>
    </div>
  );
}
